import moment from "moment";
import { getCostsByLevels, getCostsSummaryCards } from "@/api/financialReport";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

const YEARS_SHOWED = 4;

// Cuentas level_2 cuya suma forma el numerador del ratio "% Costos / Ventas".
// Coincide con el cálculo de Margen Bruto en el Estado de Resultados
// (ventas_netas - costos_directos), expresado acá como costos / ventas.
const COST_RATIO_L2_ACCOUNTS = [
    "Costos Productos",
    "Costos Variables",
    "Costos Fijos",
    "Costos Directos",
];

const state = {
    selectedCounterpartTypes: [],
    monthHeaders: [],
    accountsData: {},
    level2Accounts: [],
    level3Accounts: [],
    hierarchy: {},
    salesByPeriod: {},
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
};

const getters = {
    isCuentasMode: (state) => state.selectedCounterpartTypes.length === 0,

    groupByParam: (state) => {
        if (state.selectedCounterpartTypes.length === 0) return "cuentas";
        return state.selectedCounterpartTypes.join(",");
    },

    tableRows: (state) => {
        const rows = [];
        state.level2Accounts.forEach(l2 => {
            rows.push({
                label: l2,
                level: 2,
                isTotal: false,
                values: state.monthHeaders.map(
                    (header) => state.accountsData[l2]?.[header] ?? 0
                ),
            });

            const children = state.hierarchy[l2] || [];
            children.forEach(l3 => {
                if (l3 === l2) return;
                rows.push({
                    label: l3,
                    level: 3,
                    parent: l2,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[l3]?.[header] ?? 0
                    ),
                });
            });
        });
        return rows;
    },

    totalRow: (state) => {
        const values = state.monthHeaders.map((header) =>
            state.level2Accounts.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                return sum + (isNaN(val) ? 0 : val);
            }, 0)
        );
        return { label: "Costos Totales", values, isTotal: true };
    },

    /**
     * Ratio costos operacionales / ventas netas por período. Numerador = suma
     * de las level_2 de costos presentes en COST_RATIO_L2_ACCOUNTS. Valores
     * como ratio decimal (0.45 = 45%); se formatea en la UI con formatPercent.
     */
    ratioRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            const costos = COST_RATIO_L2_ACCOUNTS.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
            const ventas = Number(state.salesByPeriod[header] || 0);
            return ventas !== 0 ? costos / ventas : 0;
        });
        return { label: "% Costos / Ventas", values, isRatio: true };
    },

    level2RatioRows: (state) => {
        return state.level2Accounts.map((account) => {
            const values = state.monthHeaders.map((header) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                const ventas = Number(state.salesByPeriod[header] || 0);
                const costo = isNaN(val) ? 0 : val;
                return ventas !== 0 ? costo / ventas : 0;
            });
            return { label: `${account} / Ventas (%)`, values, isRatio: true };
        });
    },
};

const mutations = {
    SET_SELECTED_COUNTERPART_TYPES(state, types) {
        state.selectedCounterpartTypes = Array.isArray(types) ? [...types] : [];
    },
    SET_COSTS_DATA(state, { level2Accounts, level3Accounts, hierarchy, accountsData, monthHeaders, periodRange, salesByPeriod }) {
        state.level2Accounts = level2Accounts;
        state.level3Accounts = level3Accounts;
        state.hierarchy = hierarchy;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.salesByPeriod = salesByPeriod || {};
    },
    SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
    SET_ERROR(state, error) { state.error = error; },
    SET_SUMMARY_CARDS(state, summary) { state.summaryCards = summary; },
    SET_SUMMARY_LOADING(state, isLoading) { state.isSummaryLoading = isLoading; },
    SET_SUMMARY_ERROR(state, error) { state.summaryError = error; },
};

const actions = {
    async fetchCostsData({ getters, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            let { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange
            );

            if (filterPeriod === "year" || filterPeriod === "ytd") {
                initDate = moment(endMonth)
                    .subtract(YEARS_SHOWED, "year")
                    .startOf("year")
                    .format("YYYY-MM-DD");
            }

            const response = await getCostsByLevels(initDate, endDate, getters.groupByParam, filterPeriod);
            const {
                report_data,
                level2_accounts,
                level3_accounts,
                hierarchy,
                sales_by_period,
            } = response.data;

            const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);
            const accountsData = {};

            Object.keys(report_data).forEach(accountName => {
                accountsData[accountName] = {};
                report_data[accountName].forEach(row => {
                    const header = formatPeriodLabel(row.period, filterPeriod);
                    const amount = Number(row.total_amount);
                    accountsData[accountName][header] = isNaN(amount) ? 0 : amount;
                });
            });

            const salesByPeriod = {};
            (sales_by_period || []).forEach(row => {
                const header = formatPeriodLabel(row.period, filterPeriod);
                const amount = Number(row.total_amount);
                salesByPeriod[header] = isNaN(amount) ? 0 : amount;
            });

            commit("SET_COSTS_DATA", {
                level2Accounts: level2_accounts,
                level3Accounts: level3_accounts,
                hierarchy,
                accountsData,
                monthHeaders,
                periodRange: { init_date: initDate, end_date: endDate },
                salesByPeriod,
            });
        } catch (error) {
            console.error("[costsReport] Error fetching costs data:", error);
            commit("SET_ERROR", "Error al cargar costos.");
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchCostsSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const response = await getCostsSummaryCards(endDate);
            commit("SET_SUMMARY_CARDS", Object.freeze(response.data));
        } catch (error) {
            console.error("[costsReport] Error fetching summary cards:", error);
            commit(
                "SET_SUMMARY_ERROR",
                error?.response?.data?.detail ?? "Error al cargar resumen de costos."
            );
        } finally {
            commit("SET_SUMMARY_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
